/**
 * Shared Utilities
 *
 * Domain-agnostic utility functions used across the application.
 */

// =============================================================================
// Type Utilities
// =============================================================================

/**
 * Make all properties in T optional recursively
 */
export type DeepPartial<T> = T extends object
  ? { [P in keyof T]?: DeepPartial<T[P]> }
  : T;

/**
 * Make specified keys required
 */
export type RequiredKeys<T, K extends keyof T> = T & Required<Pick<T, K>>;

/**
 * Extract the value type from an array type
 */
export type ArrayElement<T> = T extends readonly (infer U)[] ? U : never;

/**
 * Ensure a type is not null or undefined
 */
export type NonNullable<T> = T extends null | undefined ? never : T;

// =============================================================================
// Array Utilities
// =============================================================================

/**
 * Move an item in an array from one index to another
 */
export function moveArrayItem<T>(array: T[], fromIndex: number, toIndex: number): T[] {
  const result = [...array];
  const [removed] = result.splice(fromIndex, 1);
  if (removed !== undefined) {
    result.splice(toIndex, 0, removed);
  }
  return result;
}

/**
 * Remove an item from an array by index
 */
export function removeArrayItem<T>(array: T[], index: number): T[] {
  return array.filter((_, i) => i !== index);
}

/**
 * Insert an item into an array at a specific index
 */
export function insertArrayItem<T>(array: T[], index: number, item: T): T[] {
  const result = [...array];
  result.splice(index, 0, item);
  return result;
}

/**
 * Update an item in an array by index
 */
export function updateArrayItem<T>(array: T[], index: number, updates: Partial<T>): T[] {
  return array.map((item, i) => (i === index ? { ...item, ...updates } : item));
}

/**
 * Group an array by a key
 */
export function groupBy<T, K extends string | number | symbol>(
  array: T[],
  keyFn: (item: T) => K
): Record<K, T[]> {
  return array.reduce(
    (acc, item) => {
      const key = keyFn(item);
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(item);
      return acc;
    },
    {} as Record<K, T[]>
  );
}

/**
 * Sort an array by a numeric key (for displayOrder fields)
 */
export function sortByOrder<T extends { displayOrder?: number }>(array: T[]): T[] {
  return [...array].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
}

// =============================================================================
// Object Utilities
// =============================================================================

/**
 * Deep clone an object
 */
export function deepClone<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj)) as T;
}

/**
 * Check if two objects are deeply equal
 */
export function deepEqual<T>(a: T, b: T): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Pick specific keys from an object
 */
export function pick<T extends object, K extends keyof T>(obj: T, keys: K[]): Pick<T, K> {
  const result = {} as Pick<T, K>;
  for (const key of keys) {
    if (key in obj) {
      result[key] = obj[key];
    }
  }
  return result;
}

/**
 * Omit specific keys from an object
 */
export function omit<T extends object, K extends keyof T>(obj: T, keys: K[]): Omit<T, K> {
  const result = { ...obj };
  for (const key of keys) {
    delete result[key];
  }
  return result as Omit<T, K>;
}

// =============================================================================
// String Utilities
// =============================================================================

/**
 * Capitalize the first letter of a string
 */
export function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Convert a string to title case
 */
export function toTitleCase(str: string): string {
  return str.replace(/\b\w/g, (char) => char.toUpperCase());
}

/**
 * Convert a string to kebab-case
 */
export function toKebabCase(str: string): string {
  return str
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .replace(/\s+/g, '-')
    .toLowerCase();
}

/**
 * Truncate a string to a maximum length
 */
export function truncate(str: string, maxLength: number, suffix = '...'): string {
  if (str.length <= maxLength) return str;
  return str.slice(0, maxLength - suffix.length) + suffix;
}

// =============================================================================
// Validation Utilities
// =============================================================================

const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Assert a string is a valid GUID before interpolating into OData filters.
 * Throws on invalid input to prevent OData injection.
 */
export function assertGuid(value: string, label = 'id'): string {
  if (!GUID_RE.test(value)) {
    throw new Error(`Invalid GUID for ${label}: ${value}`);
  }
  return value;
}

/** Non-throwing GUID check — for branching on optional/absent lookups
 *  (e.g. an OpsGroup with no linked account) without try/catch. */
export function isGuid(value: string | null | undefined): value is string {
  return typeof value === 'string' && GUID_RE.test(value);
}

const DATE_LITERAL_RE = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d+)?Z?)?$/;

/**
 * Assert a string is a valid ISO date or datetime literal before interpolating
 * into OData filters. Throws on invalid input to prevent OData injection via
 * date parameters (e.g. `2026-01-01) or (1 eq 1`).
 */
export function assertDateLiteral(value: string, label = 'date'): string {
  if (!DATE_LITERAL_RE.test(value)) {
    throw new Error(`Invalid date literal for ${label}: ${value}`);
  }
  return value;
}

/**
 * Check if a value is defined (not null or undefined)
 */
export function isDefined<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

/**
 * Check if a string is empty or whitespace only
 */
export function isEmptyString(value: string | null | undefined): boolean {
  return !value || value.trim().length === 0;
}

/**
 * Check if an array is empty
 */
export function isEmptyArray<T>(value: T[] | null | undefined): boolean {
  return !value || value.length === 0;
}

// =============================================================================
// Async Utilities
// =============================================================================

/**
 * Delay execution for a specified time
 */
export function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Retry a function with exponential backoff
 */
export async function retry<T>(
  fn: () => Promise<T>,
  maxAttempts: number = 3,
  baseDelayMs: number = 1000
): Promise<T> {
  let lastError: Error | undefined;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
      if (attempt < maxAttempts) {
        await delay(baseDelayMs * Math.pow(2, attempt - 1));
      }
    }
  }

  throw lastError;
}

/**
 * Debounce a function
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  fn: T,
  delayMs: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  return (...args: Parameters<T>) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => fn(...args), delayMs);
  };
}

// =============================================================================
// Error Utilities
// =============================================================================

/**
 * Safely execute a function and return a result tuple
 */
export function tryCatch<T>(fn: () => T): [T, null] | [null, Error] {
  try {
    return [fn(), null];
  } catch (error) {
    return [null, error instanceof Error ? error : new Error(String(error))];
  }
}

/**
 * Async version of tryCatch
 */
export async function tryCatchAsync<T>(
  fn: () => Promise<T>
): Promise<[T, null] | [null, Error]> {
  try {
    return [await fn(), null];
  } catch (error) {
    return [null, error instanceof Error ? error : new Error(String(error))];
  }
}
